import type { AudioService } from './service';

// D251: the gesture unlock, out of main.ts (coverage-excluded) so the WIRING can be asserted —
// the D185 seam. `main.ts` used to bind `unlock()` to ONE `pointerdown` with `{ once: true }`,
// which was two assumptions at once, both false on a phone:
//   - that a pointerdown IS a user gesture. On iOS it is synthesised from `touchstart`, which
//     WebKit has never counted as one for media — `touchend` and `click` are. The one-shot
//     listener spent itself on the touch, `resume()` was refused, and nothing ever tried again.
//   - that one resume lasts. WebKit parks the context in `interrupted` for a call, the lock
//     screen or a trip to the background, and it needs resuming again afterwards.
// So every gesture kind the HTML spec calls activation-triggering unlocks, for the page's
// lifetime: `unlock()` is a state check when there is nothing to do (engine §2.6b).

/** The activation-triggering input events (HTML §6.4) — keydown, the pointer pair, and touchend/click for WebKit. */
export const UNLOCK_GESTURES = ['pointerdown', 'pointerup', 'touchend', 'keydown', 'click'] as const;

export type UnlockTarget = Pick<AudioService, 'unlock'>;

/** Bind the unlock to every gesture on `doc`; returns the unbind. Capture + passive: it must run before any handler that stops propagation, and it never prevents a default. */
export function bindAudioUnlock(doc: Document, audio: UnlockTarget): () => void {
  const handler = (): void => audio.unlock();
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  for (const type of UNLOCK_GESTURES) doc.addEventListener(type, handler, opts);
  return () => {
    for (const type of UNLOCK_GESTURES) doc.removeEventListener(type, handler, opts);
  };
}
